import { AlertTriangle, Download, Loader2, Mic, ShieldCheck, Trash2, UserX } from "lucide-react"
import { useEffect, useState } from "react"

import { Page } from "@/components/layout/page"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { useSignedIn } from "@/hooks/use-signed-in"
import { api } from "@/lib/api"
import type { Account } from "@/lib/types"

/**
 * Your account and your data, as a whole: take it home, or take it away.
 *
 * The two rights the ethics review found missing (`docs/ethics.md`): a copy of
 * everything PRAXA keeps (GDPR Art. 20), and erasure of all of it, the sign-in
 * included (Art. 17). Smaller deletes live on their own pages - a CV on Your
 * CVs, a fact on What it remembers, a posting on Jobs - and this page says so,
 * so nobody has to erase the account to remove one thing.
 */
export function AccountRoute() {
  const signedIn = useSignedIn()
  const [account, setAccount] = useState<Account | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [typed, setTyped] = useState("")
  const [erased, setErased] = useState(false)

  useEffect(() => {
    let cancelled = false
    void api.account
      .me()
      .then((value) => !cancelled && setAccount(value))
      .catch((error: Error) => !cancelled && setMessage(error.message))
    return () => {
      cancelled = true
    }
  }, [])

  async function run(key: string, work: () => Promise<string>) {
    if (busy) return
    setBusy(key)
    setMessage(null)
    try {
      setMessage(await work())
    } catch (error) {
      setMessage((error as Error).message)
    } finally {
      setBusy(null)
    }
  }

  function download() {
    void run("export", async () => {
      await api.account.download()
      return "Downloaded. The file holds everything PRAXA keeps for this account."
    })
  }

  function clearInterviews() {
    void run("interviews", async () => {
      const { removed } = await api.interview.clearHistory()
      return removed === 1
        ? "One interview deleted."
        : `${removed} interviews deleted. Your progress figures start again.`
    })
  }

  function erase() {
    void run("erase", async () => {
      const result = await api.account.erase()
      setErased(true)
      setTyped("")
      if (signedIn) {
        // The data is gone; leave nothing signed in either.
        window.setTimeout(() => void signedIn.signOut(), 2500)
      }
      return result.signInRemoved || !account?.signInRequired
        ? `Erased: ${result.rowsDeleted} records deleted.${signedIn ? " Signing you out…" : ""}`
        : `Your data is erased (${result.rowsDeleted} records). The sign-in itself could not be removed automatically – the operator will remove it.`
    })
  }

  return (
    <Page
      tone="data"
      eyebrow="Your data"
      title="Your account"
      description="Download everything PRAXA keeps about you, or erase it all."
    >
      <div className="mx-auto w-full max-w-3xl space-y-5 px-5 py-6 lg:px-8">
        {message ? (
          <p className="rounded-lg border bg-card/50 px-4 py-3 text-sm text-muted-foreground" role="status">
            {message}
          </p>
        ) : null}

        <section className="rounded-2xl border bg-card/50 p-5">
          <p className="text-sm text-muted-foreground">Signed in as</p>
          <p className="mt-0.5 font-medium">
            {account?.email ?? (account ? "the local user (sign-in is off on this server)" : "…")}
          </p>
          <p className="mt-3 flex items-start gap-2 text-sm text-muted-foreground">
            <ShieldCheck className="mt-0.5 size-4 shrink-0 text-success" aria-hidden />
            Only this account can see its CVs, jobs, interviews and documents – the database
            itself checks the owner of every row. How your data is handled, and what is not done
            yet, is written down in the project&apos;s ethics document.
          </p>
        </section>

        <section className="space-y-3 rounded-2xl border bg-card/50 p-5">
          <div>
            <h2 className="font-semibold">Download your data</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              One JSON file with every record this account owns: CVs, target roles, saved jobs,
              interviews and scores, documents, remembered facts, ratings and daily usage.
            </p>
          </div>
          <Button variant="outline" onClick={download} disabled={busy !== null}>
            {busy === "export" ? <Loader2 className="animate-spin" aria-hidden /> : <Download aria-hidden />}
            Download my data
          </Button>
        </section>

        <section className="space-y-3 rounded-2xl border bg-card/50 p-5">
          <div>
            <h2 className="font-semibold">Delete your interview history</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Every practised interview, with its answers and scores. The rest of your account
              stays. To delete one CV, a remembered fact or a saved posting, use the delete button
              on Your CVs, What it remembers or Jobs.
            </p>
          </div>
          <Button variant="outline" onClick={clearInterviews} disabled={busy !== null || erased}>
            {busy === "interviews" ? (
              <Loader2 className="animate-spin" aria-hidden />
            ) : (
              <Mic aria-hidden />
            )}
            Delete interview history
          </Button>
        </section>

        <section className="space-y-3 rounded-2xl border border-destructive/40 bg-destructive/5 p-5">
          <div>
            <h2 className="flex items-center gap-2 font-semibold">
              <UserX className="size-4 text-destructive" aria-hidden />
              Erase your account
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Deletes everything this account owns, in every table, and then the sign-in itself.
              It cannot be undone – download your data first if you want a copy.
            </p>
          </div>
          <p className="flex items-start gap-2 text-sm">
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-destructive" aria-hidden />
            Type <span className="font-mono font-semibold">DELETE</span> to confirm.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <Input
              value={typed}
              onChange={(event) => setTyped(event.target.value)}
              placeholder="DELETE"
              aria-label="Type DELETE to confirm"
              className="max-w-40 font-mono"
              disabled={erased}
            />
            <Button
              variant="destructive"
              onClick={erase}
              disabled={typed !== "DELETE" || busy !== null || erased}
            >
              {busy === "erase" ? <Loader2 className="animate-spin" aria-hidden /> : <Trash2 aria-hidden />}
              Erase everything
            </Button>
          </div>
        </section>
      </div>
    </Page>
  )
}
